import type { CSSProperties } from "react";
import { cn } from "@/lib/utils";

/** Pulse block for API-driven UI that has not resolved yet. */
export function Skeleton({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "block animate-pulse rounded-[6px] bg-[rgba(26,24,22,0.08)]",
        className,
      )}
      style={style}
    />
  );
}

/** Rounded CTA button placeholder on checkout success/cancel return screens. */
export function CheckoutCtaSkeleton() {
  return (
    <Skeleton
      className="mx-auto mt-6 h-[46px] w-full max-w-[220px] rounded-full bg-[rgba(26,24,22,0.1)]"
    />
  );
}

/** Category chip row placeholder on /tools. */
export function ChipRowSkeleton({ count = 5 }: { count?: number }) {
  const widths = [52, 88, 72, 96, 64];
  return (
    <div
      className="no-scrollbar -mx-0.5 mb-4 flex gap-2 overflow-x-auto px-0.5 pb-4 pt-0.5"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <Skeleton
          key={i}
          className="h-[38px] flex-none rounded-full bg-[rgba(26,24,22,0.1)]"
          style={{ width: widths[i % widths.length] }}
        />
      ))}
    </div>
  );
}

/** Single tool tile matching `<ToolCard>` proportions. */
export function ToolCardSkeleton() {
  return (
    <div
      className="flex flex-col gap-2.5 rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface px-[18px] pb-4 pt-[18px]"
      aria-hidden="true"
    >
      <Skeleton className="h-11 w-11 rounded-[14px] bg-[rgba(26,24,22,0.1)]" />
      <Skeleton className="h-3 w-16" />
      <Skeleton className="h-4 w-[70%]" />
      <Skeleton className="h-3 w-full bg-[rgba(26,24,22,0.06)]" />
      <Skeleton className="h-3 w-[85%] bg-[rgba(26,24,22,0.06)]" />
    </div>
  );
}

export function ToolGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="grid gap-3 pb-2 [grid-template-columns:repeat(auto-fill,minmax(270px,1fr))]"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <ToolCardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Project summary card matching `<ProjectCard>` proportions. */
export function ProjectCardSkeleton() {
  return (
    <div
      className="rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface p-[18px]"
      aria-hidden="true"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <Skeleton className="h-4 w-[45%]" />
        <Skeleton className="h-3 w-14 bg-[rgba(26,24,22,0.06)]" />
      </div>
      <Skeleton className="mb-4 h-3 w-[80%] bg-[rgba(26,24,22,0.06)]" />
      <div className="flex flex-col">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-center gap-2.5 border-t border-[rgba(26,24,22,0.07)] py-2.5"
          >
            <Skeleton className="h-2 w-2 flex-none rounded-full bg-[rgba(26,24,22,0.12)]" />
            <span className="min-w-0 flex-1 space-y-1.5">
              <Skeleton
                className="h-[13px]"
                style={{ width: `${72 - i * 10}%` }}
              />
              <Skeleton
                className="h-[11px] bg-[rgba(26,24,22,0.06)]"
                style={{ width: `${48 - i * 6}%` }}
              />
            </span>
            <Skeleton className="h-3 w-10 bg-[rgba(26,24,22,0.06)]" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProjectGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div
      className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <ProjectCardSkeleton key={i} />
      ))}
    </div>
  );
}

/** Rows matching `<HistoryListItem>` / project generation list. */
export function HistoryListSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      className="overflow-hidden rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface"
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-[rgba(26,24,22,0.07)] px-[18px] py-[13px] last:border-b-0"
        >
          <span className="min-w-0 flex-1 space-y-1.5">
            <Skeleton
              className="h-[13px]"
              style={{ width: `${68 - (i % 3) * 10}%` }}
            />
            <Skeleton
              className="h-[11px] bg-[rgba(26,24,22,0.06)]"
              style={{ width: `${42 - (i % 3) * 6}%` }}
            />
          </span>
          <Skeleton className="h-3 w-12 bg-[rgba(26,24,22,0.06)]" />
        </div>
      ))}
    </div>
  );
}

/** /tools/[tool] body while the tool detail request is in flight. */
export function ToolDetailSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="mb-3 flex items-center gap-[15px]">
        <Skeleton className="h-[46px] w-[46px] rounded-[14px] bg-[rgba(26,24,22,0.1)]" />
        <Skeleton className="h-7 w-[55%]" />
      </div>
      <Skeleton className="mb-2 h-3.5 w-full bg-[rgba(26,24,22,0.06)]" />
      <Skeleton className="mb-5 h-3.5 w-[85%] bg-[rgba(26,24,22,0.06)]" />
      <div className="flex flex-wrap items-start gap-[26px]">
        <div className="min-w-0 flex-[999_1_440px]">
          <div className="card-shadow rounded-[20px] border border-[rgba(26,24,22,0.08)] bg-white p-6">
            {[0, 1, 2].map((i) => (
              <div key={i} className="mb-[18px] last:mb-0">
                <Skeleton className="mb-2 h-3 w-24" />
                <Skeleton className="h-[47px] w-full rounded-[14px] bg-[rgba(26,24,22,0.06)]" />
              </div>
            ))}
            <Skeleton className="mt-2 h-12 w-full rounded-full bg-[rgba(26,24,22,0.1)]" />
          </div>
        </div>
        <aside className="flex min-w-[270px] flex-[1_1_280px] flex-col gap-3">
          <Skeleton className="h-3 w-28" />
          <ToolCardSkeleton />
        </aside>
      </div>
    </div>
  );
}

/** /projects/[project] shell while the project request is in flight. */
export function ProjectDetailSkeleton() {
  return (
    <div
      className="flex flex-col gap-5 md:flex-row md:items-start md:gap-7"
      aria-hidden="true"
    >
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex items-center justify-between gap-4">
          <Skeleton className="h-8 w-[45%]" />
          <Skeleton className="h-9 w-24 rounded-[10px] bg-[rgba(26,24,22,0.06)]" />
        </div>
        <Skeleton className="mb-3 h-3.5 w-full max-w-[420px] bg-[rgba(26,24,22,0.06)]" />
        <Skeleton className="mb-[22px] h-3.5 w-full max-w-[280px] bg-[rgba(26,24,22,0.06)]" />
        <HistoryListSkeleton count={5} />
      </div>
      <aside className="box-border w-full flex-none md:w-[320px]">
        <div className="box-border rounded-[20px] border border-[rgba(26,24,22,0.05)] bg-surface px-[18px] pb-5 pt-[18px]">
          <div className="mb-[15px] flex items-center gap-2.5">
            <Skeleton className="h-7 w-7 rounded-[9px] bg-[rgba(26,24,22,0.1)]" />
            <Skeleton className="h-4 w-28" />
          </div>
          <Skeleton className="mb-2.5 h-[120px] w-full rounded-[13px] bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="mb-[11px] h-3 w-full bg-[rgba(26,24,22,0.06)]" />
          <div className="flex justify-end">
            <Skeleton className="h-[34px] w-24 rounded-[10px] bg-[rgba(26,24,22,0.1)]" />
          </div>
        </div>
      </aside>
    </div>
  );
}

/** /history/[item] shell while the generation request is in flight. */
export function HistoryItemSkeleton() {
  return (
    <div
      className="flex flex-wrap items-start gap-[26px]"
      aria-hidden="true"
    >
      <div className="min-w-0 flex-[999_1_440px]">
        <Skeleton className="mb-2 h-7 w-[55%]" />
        <Skeleton className="mb-[18px] h-3 w-40 bg-[rgba(26,24,22,0.06)]" />
        <div className="card-shadow rounded-[20px] border border-[rgba(26,24,22,0.08)] bg-white px-[26px] pb-5 pt-[26px]">
          <Skeleton className="mb-4 h-4 w-[40%]" />
          <Skeleton className="mb-2.5 h-3.5 w-full bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="mb-2.5 h-3.5 w-full bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="mb-2.5 h-3.5 w-[92%] bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="mb-2.5 h-3.5 w-[88%] bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="h-3.5 w-[70%] bg-[rgba(26,24,22,0.06)]" />
          <Skeleton className="mt-5 h-3 w-48 bg-[rgba(26,24,22,0.05)]" />
        </div>
      </div>
      <aside className="flex min-w-[270px] flex-[1_1_280px] flex-col">
        <Skeleton className="h-[42px] w-full rounded-full bg-[rgba(26,24,22,0.1)]" />
        <Skeleton className="mb-2 mt-5 h-3 w-28" />
        <ToolCardSkeleton />
      </aside>
    </div>
  );
}

/**
 * Generic page-shaped placeholder for auth hydration and route Suspense
 * fallbacks — title + body blocks, not a centered spinner.
 *
 * Deliberately *not* animated: React throws this subtree away when the
 * boundary resolves, so an entrance animation here would replay on the real
 * content's fresh DOM node and read as the page twitching twice.
 */
export function PageContentSkeleton() {
  return (
    <section
      className="px-5 pt-7"
      role="status"
      aria-label="Loading"
    >
      <Skeleton className="mb-2 h-8 w-[42%] max-w-[280px]" />
      <Skeleton className="mb-5 h-3.5 w-full max-w-[420px] bg-[rgba(26,24,22,0.06)]" />
      <div className="space-y-3" aria-hidden="true">
        <Skeleton className="h-[72px] w-full rounded-[16px] bg-[rgba(26,24,22,0.06)]" />
        <Skeleton className="h-[72px] w-full rounded-[16px] bg-[rgba(26,24,22,0.06)]" />
        <Skeleton className="h-[72px] w-full max-w-[90%] rounded-[16px] bg-[rgba(26,24,22,0.06)]" />
      </div>
    </section>
  );
}
